import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

const useConversationStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    panel: {
      position: 'relative',
      width: '100%',
      maxWidth: 460,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      boxSizing: 'border-box',
      minWidth: 0,
      color: t.text,
      '& $lineText': { color: t.text },
      '& $lineTime': { color: t.text3 },
      '& $lineInterim $lineText, & $partialTail': { color: t.text2 },
    },
    panelStage: {
      position: 'relative',
      isolation: 'isolate',
      flex: '1 1 auto',
      minHeight: 120,
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: 16,
      background: t.glass,
      boxShadow: `inset 0 0 0 1px ${t.glassBorder}`,
      color: t.onGround,
      '& $lineText': { color: t.onGround },
      '& $lineTime': { color: t.onGroundFaint },
      '& $lineInterim $lineText, & $partialTail': { color: t.onGroundMuted },
    },
    stageHeader: {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: '10px 14px',
      borderBottom: `1px solid ${t.glassBorder}`,
    },
    stageTitle: {
      margin: 0,
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.heavy,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: t.onGroundMuted,
    },
    liveStatus: {
      marginLeft: 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.stageCall,
    },
    liveDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: t.stageCall,
    },
    label: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.text2,
    },
    scroll: {
      maxHeight: 260,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      paddingRight: theme.spacing(0.5),
      scrollbarWidth: 'thin',
      '&::-webkit-scrollbar': {
        width: 4,
      },
      '&::-webkit-scrollbar-thumb': {
        background: t.lineStrong,
        borderRadius: 2,
      },
    },
    scrollStage: {
      maxHeight: 'none',
      flex: '1 1 auto',
      minHeight: 0,
      padding: '12px 14px',
      scrollbarColor: `${t.glassBorder} transparent`,
      '&::-webkit-scrollbar-thumb': {
        background: t.glassBorder,
      },
      '&:focus-visible': {
        outline: `2px solid ${t.stageFocus}`,
        outlineOffset: -2,
        borderRadius: 16,
      },
    },
    emptyState: {
      margin: 'auto',
      maxWidth: 300,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
      padding: theme.spacing(2),
      textAlign: 'center',
      ...CS_FONT.small,
      color: t.onGroundMuted,
    },
    emptyTitle: {
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.strong,
      color: t.onGround,
    },
    line: {
      display: 'grid',
      gridTemplateColumns: '3px minmax(0, 1fr)',
      columnGap: 10,
      minWidth: 0,
      borderRadius: 6,
      transition: 'background-color 200ms ease-out',
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: 2,
      },
    },
    lineInterim: {},
    rail: {
      borderRadius: 2,
      background: 'var(--cs-tone)',
    },
    lineBody: {
      minWidth: 0,
    },
    lineMeta: {
      display: 'flex',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: 6,
      minWidth: 0,
    },
    lineName: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.heavy,
      color: 'var(--cs-tone)',
      overflowWrap: 'anywhere',
    },
    lineTime: {
      ...CS_FONT.caption,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
    },
    lineText: {
      margin: '2px 0 0',
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.regular,
      maxWidth: '62ch',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
    },
    partialTail: {},
    groupHighlighted: {
      backgroundColor: alpha(t.info, 0.16),
    },
    liveCaret: {
      display: 'inline-block',
      width: 2,
      height: '0.95em',
      marginLeft: 3,
      verticalAlign: 'text-bottom',
      background: 'currentColor',
      animation: '$caretBlink 1.2s steps(1, end) infinite',
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        opacity: 0.6,
      },
    },
    '@keyframes caretBlink': {
      '0%, 45%': { opacity: 1 },
      '50%, 95%': { opacity: 0.1 },
      '100%': { opacity: 1 },
    },
    emptyText: {
      ...CS_FONT.small,
      color: t.text2,
    },
    jumpPill: {
      position: 'relative',
      alignSelf: 'center',
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 32,
      margin: theme.spacing(0, 0, 1.25),
      padding: theme.spacing(0, 1.5),
      border: 'none',
      borderRadius: 999,
      background: t.text,
      color: t.surface,
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '& svg': { fontSize: 16 },
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: '-6px 0',
      },
      '&:hover': { background: t.text2 },
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: 2,
      },
      '$panelStage &': {
        background: t.onGround,
        color: t.ground,
        '&:hover': { background: t.onGroundMuted },
        '&:focus-visible': { outlineColor: t.stageFocus },
      },
    },
  };
});

export default useConversationStyles;
